import React, { useState, useEffect, useCallback } from "react";
import {
  DynamicReportColumn,
  DynamicReportDataSource,
  DynamicReportFilter,
  DynamicReportAggregation,
  DynamicReportRequest,
  DynamicReportResponse,
  ReportTemplate,
} from "../types";
import {
  dynamicReportService,
  reportTemplateService,
} from "../services/dataService";
import { formatCurrency, formatNumber } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";
import "./DynamicReports.css";

type ChartType = "bar" | "line" | "pie" | "area";
type ViewMode = "table" | "chart" | "both";
type SideSection = "fields" | "aggregations" | "filters" | "sort";

const CHART_COLORS = [
  "#667eea", "#764ba2", "#f093fb", "#f5576c", "#4facfe",
  "#00f2fe", "#43e97b", "#fa709a", "#fee140", "#30cfd0",
  "#a18cd1", "#fbc2eb", "#ff9a9e", "#fad0c4", "#a1c4fd",
  "#c2e9fb", "#d4fc79", "#96e6a1", "#84fab0", "#8fd3f4",
];

type ReportMode = "detail" | "grouped";

const FILTER_OPERATORS: Record<string, { label: string; types: string[] }> = {
  eq: { label: "Eşit", types: ["string", "int", "float", "decimal", "bool", "datetime"] },
  neq: { label: "Eşit Değil", types: ["string", "int", "float", "decimal", "bool", "datetime"] },
  gt: { label: "Büyük", types: ["int", "float", "decimal", "datetime"] },
  gte: { label: "Büyük Eşit", types: ["int", "float", "decimal", "datetime"] },
  lt: { label: "Küçük", types: ["int", "float", "decimal", "datetime"] },
  lte: { label: "Küçük Eşit", types: ["int", "float", "decimal", "datetime"] },
  contains: { label: "İçerir", types: ["string"] },
  startswith: { label: "İle Başlar", types: ["string"] },
  between: { label: "Arasında", types: ["int", "float", "decimal", "datetime"] },
};

const AGG_FUNCTIONS = [
  { value: "sum", label: "Toplam" },
  { value: "count", label: "Adet" },
  { value: "avg", label: "Ortalama" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maksimum" },
];

const DynamicReports: React.FC = () => {
  // Data sources
  const [dataSources, setDataSources] = useState<DynamicReportDataSource[]>([]);
  const [selectedDataSource, setSelectedDataSource] = useState<string>("");
  const [columns, setColumns] = useState<DynamicReportColumn[]>([]);
  const [loadingDS, setLoadingDS] = useState(false);

  // Report mode
  const [reportMode, setReportMode] = useState<ReportMode>("detail");

  // Detail mode
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  // Grouped mode
  const [groupByColumns, setGroupByColumns] = useState<string[]>([]);
  const [aggregations, setAggregations] = useState<DynamicReportAggregation[]>([]);

  // Filters
  const [filters, setFilters] = useState<DynamicReportFilter[]>([]);

  // Sorting
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDescending, setSortDescending] = useState(true);

  // Pagination
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);

  // Results
  const [result, setResult] = useState<DynamicReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Chart state
  const [viewMode, setViewMode] = useState<ViewMode>("both");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [selectedChartMetrics, setSelectedChartMetrics] = useState<string[]>([]);

  // Templates
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [templatePublic, setTemplatePublic] = useState(true);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Sidebar & accordion
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<SideSection, boolean>>({
    fields: false,
    aggregations: false,
    filters: false,
    sort: false,
  });

  const toggleSection = (section: SideSection) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Alert / Confirm
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({ isOpen: false, message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  // Load data sources on mount
  useEffect(() => {
    loadDataSources();
    loadTemplates();
  }, []);

  // Load columns when data source changes
  useEffect(() => {
    if (selectedDataSource) {
      loadColumns(selectedDataSource);
    } else {
      setColumns([]);
    }
    // Reset selections
    setSelectedColumns([]);
    setGroupByColumns([]);
    setAggregations([]);
    setFilters([]);
    setSortBy("");
    setResult(null);
    setPageNumber(1);
  }, [selectedDataSource]);

  const loadDataSources = async () => {
    try {
      setLoadingDS(true);
      const data = await dynamicReportService.getDataSources();
      setDataSources(data);
    } catch (error) {
      console.error("Veri kaynakları yüklenemedi:", error);
    } finally {
      setLoadingDS(false);
    }
  };

  const loadColumns = async (dsName: string) => {
    try {
      setLoadingDS(true);
      const data = await dynamicReportService.getColumns(dsName);
      setColumns(data.availableColumns || []);
    } catch (error) {
      console.error("Kolonlar yüklenemedi:", error);
    } finally {
      setLoadingDS(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const data = await reportTemplateService.getAll();
      setTemplates(data);
    } catch (error) {
      console.error("Şablonlar yüklenemedi:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Column helpers
  const filterableColumns = columns.filter((c) => c.isFilterable);
  const groupableColumns = columns.filter((c) => c.isGroupable);
  const aggregatableColumns = columns.filter((c) => c.isAggregatable);
  const sortableColumns = columns.filter((c) => c.isSortable);

  const getColumnDisplayName = (fieldName: string): string => {
    const col = columns.find((c) => c.fieldName === fieldName);
    return col?.displayName || fieldName;
  };

  const getColumnType = (fieldName: string): string => {
    const col = columns.find((c) => c.fieldName === fieldName);
    return col?.dataType || "string";
  };

  const getOperatorsForField = (fieldName: string) => {
    const dataType = getColumnType(fieldName);
    return Object.entries(FILTER_OPERATORS)
      .filter(([, op]) => op.types.includes(dataType))
      .map(([key, op]) => ({ value: key, label: op.label }));
  };

  // Column toggle
  const toggleColumn = (fieldName: string) => {
    setSelectedColumns((prev) =>
      prev.includes(fieldName)
        ? prev.filter((c) => c !== fieldName)
        : [...prev, fieldName]
    );
  };

  const toggleAllColumns = () => {
    if (selectedColumns.length === columns.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(columns.map((c) => c.fieldName));
    }
  };

  // Group by toggle
  const toggleGroupBy = (fieldName: string) => {
    setGroupByColumns((prev) =>
      prev.includes(fieldName)
        ? prev.filter((c) => c !== fieldName)
        : [...prev, fieldName]
    );
  };

  // Aggregation management
  const addAggregation = () => {
    if (aggregatableColumns.length === 0) return;
    setAggregations((prev) => [
      ...prev,
      {
        field: aggregatableColumns[0].fieldName,
        function: "sum",
        alias: `${aggregatableColumns[0].displayName}_Toplam`,
      },
    ]);
  };

  const updateAggregation = (index: number, updates: Partial<DynamicReportAggregation>) => {
    setAggregations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeAggregation = (index: number) => {
    setAggregations((prev) => prev.filter((_, i) => i !== index));
  };

  // Filter management
  const addFilter = () => {
    if (filterableColumns.length === 0) return;
    setFilters((prev) => [
      ...prev,
      { field: filterableColumns[0].fieldName, operator: "eq", value: "" },
    ]);
  };

  const updateFilter = (index: number, updates: Partial<DynamicReportFilter>) => {
    setFilters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeFilter = (index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  // Build request
  const buildRequest = (page: number = pageNumber): DynamicReportRequest => {
    const validFilters = filters.filter((f) => f.value.trim() !== "");
    if (reportMode === "grouped") {
      return {
        dataSource: selectedDataSource,
        selectedColumns: [],
        filters: validFilters,
        groupByColumns: groupByColumns.length > 0 ? groupByColumns : null,
        aggregations: aggregations.length > 0 ? aggregations : null,
        sortBy: sortBy || undefined,
        sortDescending,
        pageNumber: page,
        pageSize,
      };
    }
    return {
      dataSource: selectedDataSource,
      selectedColumns,
      filters: validFilters,
      groupByColumns: null,
      aggregations: null,
      sortBy: sortBy || undefined,
      sortDescending,
      pageNumber: page,
      pageSize,
    };
  };

  // Execute report
  const executeReport = async (page: number = 1) => {
    if (!selectedDataSource) {
      setAlertModal({ isOpen: true, message: "Lütfen bir veri kaynağı seçin.", type: "warning" });
      return;
    }
    if (reportMode === "detail" && selectedColumns.length === 0) {
      setAlertModal({ isOpen: true, message: "Lütfen en az bir kolon seçin.", type: "warning" });
      return;
    }
    if (reportMode === "grouped" && groupByColumns.length === 0 && aggregations.length === 0) {
      setAlertModal({ isOpen: true, message: "Gruplu rapor için en az bir gruplama veya toplam alanı seçin.", type: "warning" });
      return;
    }

    setLoading(true);
    setPageNumber(page);
    try {
      const request = buildRequest(page);
      const data = await dynamicReportService.execute(request);
      setResult(data);
    } catch (error: any) {
      setAlertModal({
        isOpen: true,
        message: error?.response?.data?.message || "Rapor çalıştırılırken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Export Excel
  const handleExportExcel = async () => {
    if (!result || result.rows.length === 0) return;
    setExporting(true);
    try {
      const request = buildRequest(1);
      const blob = await dynamicReportService.exportExcel(request);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapor_${selectedDataSource}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setAlertModal({ isOpen: true, message: "Excel export sırasında hata oluştu.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  // Template save
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setAlertModal({ isOpen: true, message: "Şablon adı zorunludur.", type: "warning" });
      return;
    }
    if (!selectedDataSource) {
      setAlertModal({ isOpen: true, message: "Lütfen önce veri kaynağı seçin.", type: "warning" });
      return;
    }

    const validFilters = filters.filter((f) => f.value.trim() !== "");
    const config: any = {
      reportMode,
      selectedColumns: reportMode === "detail" ? selectedColumns : [],
      filters: validFilters,
      groupByColumns: reportMode === "grouped" ? groupByColumns : [],
      aggregations: reportMode === "grouped" ? aggregations : [],
      sortBy,
      sortDescending,
    };

    try {
      await reportTemplateService.create({
        name: templateName.trim(),
        description: templateDesc.trim() || undefined,
        dataSource: selectedDataSource,
        configJson: JSON.stringify(config),
        isPublic: templatePublic,
      });
      setAlertModal({ isOpen: true, message: "Şablon başarıyla kaydedildi.", type: "success" });
      setShowSaveTemplate(false);
      setTemplateName("");
      setTemplateDesc("");
      loadTemplates();
    } catch (error) {
      setAlertModal({ isOpen: true, message: "Şablon kaydedilirken hata oluştu.", type: "error" });
    }
  };

  // Template load
  const handleLoadTemplate = async (template: ReportTemplate) => {
    try {
      const config = JSON.parse(template.configJson);
      setSelectedDataSource(template.dataSource);

      // Wait for columns to load before applying config
      const dsData = await dynamicReportService.getColumns(template.dataSource);
      setColumns(dsData.availableColumns || []);

      setReportMode(config.reportMode || "detail");
      setSelectedColumns(config.selectedColumns || []);
      setFilters(config.filters || []);
      setGroupByColumns(config.groupByColumns || []);
      setAggregations(config.aggregations || []);
      setSortBy(config.sortBy || "");
      setSortDescending(config.sortDescending ?? true);
      setShowTemplates(false);
      setResult(null);
      setPageNumber(1);
    } catch (error) {
      setAlertModal({ isOpen: true, message: "Şablon yüklenirken hata oluştu.", type: "error" });
    }
  };

  // Template run directly
  const handleRunTemplate = async (template: ReportTemplate) => {
    try {
      setLoading(true);
      setShowTemplates(false);
      const data = await reportTemplateService.execute(template.reportTemplateId);

      // Also load config to show on UI
      const config = JSON.parse(template.configJson);
      setSelectedDataSource(template.dataSource);
      const dsData = await dynamicReportService.getColumns(template.dataSource);
      setColumns(dsData.availableColumns || []);
      setReportMode(config.reportMode || "detail");
      setSelectedColumns(config.selectedColumns || []);
      setFilters(config.filters || []);
      setGroupByColumns(config.groupByColumns || []);
      setAggregations(config.aggregations || []);
      setSortBy(config.sortBy || "");
      setSortDescending(config.sortDescending ?? true);

      setResult(data);
      setPageNumber(1);
    } catch (error: any) {
      setAlertModal({
        isOpen: true,
        message: error?.response?.data?.message || "Şablon çalıştırılırken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Template delete
  const handleDeleteTemplate = (template: ReportTemplate) => {
    setConfirmModal({
      isOpen: true,
      message: `"${template.name}" şablonunu silmek istediğinize emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          await reportTemplateService.delete(template.reportTemplateId);
          setAlertModal({ isOpen: true, message: "Şablon silindi.", type: "success" });
          loadTemplates();
        } catch (error) {
          setAlertModal({ isOpen: true, message: "Şablon silinirken hata oluştu.", type: "error" });
        }
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  // Template export excel
  const handleExportTemplate = async (template: ReportTemplate) => {
    setExporting(true);
    try {
      const blob = await reportTemplateService.exportExcel(template.reportTemplateId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${template.name.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setAlertModal({ isOpen: true, message: "Export sırasında hata oluştu.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  // Metadata-based column type helpers (handles null first rows)
  const isNumericColumn = (colName: string): boolean => {
    const col = columns.find((c) => c.fieldName === colName);
    if (col) return ["int", "float", "decimal"].includes(col.dataType);
    // Fallback: scan rows for a non-null value
    if (result) {
      for (const row of result.rows) {
        if (row[colName] != null) return typeof row[colName] === "number";
      }
    }
    return false;
  };

  const isStringColumn = (colName: string): boolean => {
    const col = columns.find((c) => c.fieldName === colName);
    if (col) return col.dataType === "string";
    if (result) {
      for (const row of result.rows) {
        if (row[colName] != null) return typeof row[colName] === "string";
      }
    }
    return false;
  };

  // KPI computation from result data
  const computeKPIs = (): { label: string; value: string; color: string }[] => {
    if (!result || result.rows.length === 0) return [];
    const kpiColors = ["#667eea", "#059669", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

    if (reportMode === "grouped" && aggregations.length > 0) {
      return aggregations.slice(0, 6).map((agg, i) => {
        const colKey = result.columns.find((c) => c.toLowerCase() === agg.alias.toLowerCase()) || agg.alias;
        // Prefer backend aggregations (full dataset), fallback to page rows
        if (result.aggregations && result.aggregations[colKey] != null) {
          return { label: agg.alias, value: formatNumber(Number(result.aggregations[colKey])), color: kpiColors[i % kpiColors.length] };
        }
        let total = 0;
        result.rows.forEach((row) => {
          const v = Number(row[colKey]);
          if (!isNaN(v)) total += v;
        });
        return { label: agg.alias, value: formatNumber(total), color: kpiColors[i % kpiColors.length] };
      });
    }

    // Detail mode: find numeric columns using metadata
    const numCols = result.columns.filter((col) => isNumericColumn(col));
    return numCols.slice(0, 4).map((col, i) => {
      // Prefer backend aggregations if available
      if (result.aggregations && result.aggregations[col] != null) {
        return { label: getColumnDisplayName(col) || col, value: formatNumber(Number(result.aggregations[col])), color: kpiColors[i % kpiColors.length] };
      }
      let total = 0;
      result.rows.forEach((row) => {
        const v = Number(row[col]);
        if (!isNaN(v)) total += v;
      });
      return { label: getColumnDisplayName(col) || col, value: formatNumber(total), color: kpiColors[i % kpiColors.length] };
    });
  };

  // Cell formatting
  const formatCell = (value: any, colName: string): string => {
    if (value == null || value === "") return "-";
    const col = columns.find((c) => c.fieldName === colName);
    const dataType = col?.dataType;
    if (dataType === "datetime" && value) {
      try {
        return new Date(value).toLocaleDateString("tr-TR");
      } catch {
        return String(value);
      }
    }
    if (dataType === "bool") return value ? "Evet" : "Hayır";
    if (dataType === "decimal" || dataType === "float") {
      return formatNumber(Number(value));
    }
    if (dataType === "int") return formatNumber(Number(value), 0);
    return String(value);
  };

  // For grouped results where columns might be aliases
  const formatGroupedCell = (value: any, colName: string): string => {
    if (value == null || value === "") return "-";
    if (typeof value === "number") return formatNumber(value);
    if (typeof value === "boolean") return value ? "Evet" : "Hayır";
    return String(value);
  };

  if (loadingDS && dataSources.length === 0) {
    return <PageLoader message="Veri kaynakları yükleniyor..." />;
  }

  const kpis = computeKPIs();

  return (
    <div className={`pbi-layout ${sidebarCollapsed ? "pbi-sidebar-collapsed" : ""}`}>
      {/* ─── LEFT CONFIG SIDEBAR ─── */}
      <aside className="pbi-sidebar">
        <div className="pbi-sidebar-head">
          {!sidebarCollapsed && <span className="pbi-sidebar-title">Yapılandırma</span>}
          <button className="pbi-sidebar-toggle" onClick={() => setSidebarCollapsed((p) => !p)} title={sidebarCollapsed ? "Genişlet" : "Daralt"}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {sidebarCollapsed ? <><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></> : <><polyline points="11 17 6 12 11 7"/><line x1="6" y1="12" x2="18" y2="12"/></>}
            </svg>
          </button>
        </div>

        {!sidebarCollapsed && (
          <div className="pbi-sidebar-body">
            {/* Data Source */}
            <div className="pbi-side-section pbi-ds-section">
              <label className="pbi-side-label">Veri Kaynağı</label>
              <select value={selectedDataSource} onChange={(e) => setSelectedDataSource(e.target.value)}>
                <option value="">Seçin...</option>
                {dataSources.map((ds) => (
                  <option key={ds.dataSourceName} value={ds.dataSourceName}>{ds.displayName}</option>
                ))}
              </select>

              <label className="pbi-side-label" style={{ marginTop: 12 }}>Rapor Tipi</label>
              <div className="pbi-mode-toggle">
                <button className={reportMode === "detail" ? "active" : ""} onClick={() => setReportMode("detail")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Detay
                </button>
                <button className={reportMode === "grouped" ? "active" : ""} onClick={() => setReportMode("grouped")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  Gruplu
                </button>
              </div>
            </div>

            {selectedDataSource && (
              <>
                {/* Fields / Columns */}
                <div className="pbi-side-section">
                  <button className="pbi-section-toggle" onClick={() => toggleSection("fields")}>
                    <span className="pbi-section-toggle-icon">{collapsedSections.fields ? "▸" : "▾"}</span>
                    <span>{reportMode === "detail" ? "Kolonlar" : "Gruplama Alanları"}</span>
                    <span className="pbi-badge">{reportMode === "detail" ? selectedColumns.length : groupByColumns.length}</span>
                  </button>
                  {!collapsedSections.fields && (
                    <div className="pbi-section-content">
                      {reportMode === "detail" ? (
                        <>
                          <div className="pbi-section-actions">
                            <button className="pbi-link-btn" onClick={toggleAllColumns}>
                              {selectedColumns.length === columns.length ? "Hiçbirini Seçme" : "Tümünü Seç"}
                            </button>
                          </div>
                          <div className="pbi-chips">
                            {columns.map((col) => (
                              <button key={col.fieldName} className={`pbi-chip ${selectedColumns.includes(col.fieldName) ? "active" : ""}`} onClick={() => toggleColumn(col.fieldName)}>
                                {col.displayName}
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="pbi-chips">
                          {groupableColumns.map((col) => (
                            <button key={col.fieldName} className={`pbi-chip ${groupByColumns.includes(col.fieldName) ? "active" : ""}`} onClick={() => toggleGroupBy(col.fieldName)}>
                              {col.displayName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Aggregations (grouped mode) */}
                {reportMode === "grouped" && (
                  <div className="pbi-side-section">
                    <button className="pbi-section-toggle" onClick={() => toggleSection("aggregations")}>
                      <span className="pbi-section-toggle-icon">{collapsedSections.aggregations ? "▸" : "▾"}</span>
                      <span>Toplamlar</span>
                      <span className="pbi-badge">{aggregations.length}</span>
                    </button>
                    {!collapsedSections.aggregations && (
                      <div className="pbi-section-content">
                        <div className="pbi-section-actions">
                          <button className="pbi-link-btn" onClick={addAggregation}>+ Toplam Ekle</button>
                        </div>
                        {aggregations.map((agg, idx) => (
                          <div key={idx} className="pbi-field-row">
                            <select value={agg.field} onChange={(e) => {
                              const col = aggregatableColumns.find((c) => c.fieldName === e.target.value);
                              updateAggregation(idx, { field: e.target.value, alias: `${col?.displayName || e.target.value}_${AGG_FUNCTIONS.find((f) => f.value === agg.function)?.label || ""}` });
                            }}>
                              {aggregatableColumns.map((col) => (
                                <option key={col.fieldName} value={col.fieldName}>{col.displayName}</option>
                              ))}
                            </select>
                            <select value={agg.function} onChange={(e) => {
                              const fn = AGG_FUNCTIONS.find((f) => f.value === e.target.value);
                              const col = aggregatableColumns.find((c) => c.fieldName === agg.field);
                              updateAggregation(idx, { function: e.target.value, alias: `${col?.displayName || agg.field}_${fn?.label || ""}` });
                            }}>
                              {AGG_FUNCTIONS.map((fn) => (
                                <option key={fn.value} value={fn.value}>{fn.label}</option>
                              ))}
                            </select>
                            <input type="text" value={agg.alias} onChange={(e) => updateAggregation(idx, { alias: e.target.value })} placeholder="Başlık" />
                            <button className="pbi-btn-remove" onClick={() => removeAggregation(idx)}>✕</button>
                          </div>
                        ))}
                        {aggregations.length === 0 && <p className="pbi-hint">Henüz toplam eklenmedi</p>}
                      </div>
                    )}
                  </div>
                )}

                {/* Filters */}
                <div className="pbi-side-section">
                  <button className="pbi-section-toggle" onClick={() => toggleSection("filters")}>
                    <span className="pbi-section-toggle-icon">{collapsedSections.filters ? "▸" : "▾"}</span>
                    <span>Filtreler</span>
                    <span className="pbi-badge">{filters.length}</span>
                  </button>
                  {!collapsedSections.filters && (
                    <div className="pbi-section-content">
                      <div className="pbi-section-actions">
                        <button className="pbi-link-btn" onClick={addFilter}>+ Filtre Ekle</button>
                      </div>
                      {filters.map((f, idx) => (
                        <div key={idx} className="pbi-filter-row">
                          <select value={f.field} onChange={(e) => updateFilter(idx, { field: e.target.value, operator: "eq", value: "", value2: undefined })}>
                            {filterableColumns.map((col) => (
                              <option key={col.fieldName} value={col.fieldName}>{col.displayName}</option>
                            ))}
                          </select>
                          <select value={f.operator} onChange={(e) => updateFilter(idx, { operator: e.target.value })}>
                            {getOperatorsForField(f.field).map((op) => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>
                          {getColumnType(f.field) === "datetime" ? (
                            <input type="date" value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} />
                          ) : getColumnType(f.field) === "bool" ? (
                            <select value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })}>
                              <option value="">Seçin</option>
                              <option value="true">Evet</option>
                              <option value="false">Hayır</option>
                            </select>
                          ) : (
                            <input type={["int", "float", "decimal"].includes(getColumnType(f.field)) ? "number" : "text"} value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} placeholder="Değer" />
                          )}
                          {f.operator === "between" && (
                            getColumnType(f.field) === "datetime" ? (
                              <input type="date" value={f.value2 || ""} onChange={(e) => updateFilter(idx, { value2: e.target.value })} />
                            ) : (
                              <input type="number" value={f.value2 || ""} onChange={(e) => updateFilter(idx, { value2: e.target.value })} placeholder="Üst Değer" />
                            )
                          )}
                          <button className="pbi-btn-remove" onClick={() => removeFilter(idx)}>✕</button>
                        </div>
                      ))}
                      {filters.length === 0 && <p className="pbi-hint">Filtre eklenmedi — tüm kayıtlar</p>}
                    </div>
                  )}
                </div>

                {/* Sort */}
                <div className="pbi-side-section">
                  <button className="pbi-section-toggle" onClick={() => toggleSection("sort")}>
                    <span className="pbi-section-toggle-icon">{collapsedSections.sort ? "▸" : "▾"}</span>
                    <span>Sıralama</span>
                  </button>
                  {!collapsedSections.sort && (
                    <div className="pbi-section-content">
                      <div className="pbi-sort-row">
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                          <option value="">Varsayılan</option>
                          {sortableColumns.map((col) => (
                            <option key={col.fieldName} value={col.fieldName}>{col.displayName}</option>
                          ))}
                          {reportMode === "grouped" && aggregations.map((agg) => (
                            <option key={agg.alias} value={agg.alias}>{agg.alias}</option>
                          ))}
                        </select>
                        <button className={`pbi-sort-dir ${sortDescending ? "desc" : "asc"}`} onClick={() => setSortDescending(!sortDescending)} title={sortDescending ? "Azalan" : "Artan"}>
                          {sortDescending ? "↓" : "↑"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </aside>

      {/* ─── MAIN CANVAS ─── */}
      <main className="pbi-canvas">
        {/* Toolbar */}
        <div className="pbi-toolbar">
          <div className="pbi-toolbar-left">
            <h1 className="pbi-title">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              Dinamik Raporlar
            </h1>
            {selectedDataSource && (
              <span className="pbi-ds-badge">
                {dataSources.find((d) => d.dataSourceName === selectedDataSource)?.displayName || selectedDataSource}
              </span>
            )}
            {result && <span className="pbi-count-badge">{result.totalCount} kayıt</span>}
          </div>
          <div className="pbi-toolbar-right">
            <button className="pbi-btn-execute" onClick={() => executeReport(1)} disabled={loading}>
              {loading ? <span className="pbi-spinner" /> : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              )}
              Çalıştır
            </button>

            {/* View mode */}
            <div className="pbi-toggle-group">
              <button className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")} title="Tablo">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </button>
              <button className={viewMode === "chart" ? "active" : ""} onClick={() => setViewMode("chart")} title="Grafik">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              </button>
              <button className={viewMode === "both" ? "active" : ""} onClick={() => setViewMode("both")} title="Her İkisi">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="18" height="7"/></svg>
              </button>
            </div>

            {/* Chart type */}
            {viewMode !== "table" && (
              <div className="pbi-toggle-group pbi-chart-types">
                <button className={chartType === "bar" ? "active" : ""} onClick={() => setChartType("bar")} title="Sütun">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                </button>
                <button className={chartType === "line" ? "active" : ""} onClick={() => setChartType("line")} title="Çizgi">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                </button>
                <button className={chartType === "area" ? "active" : ""} onClick={() => setChartType("area")} title="Alan">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 20L8 14L13 17L21 7V20H3Z"/></svg>
                </button>
                <button className={chartType === "pie" ? "active" : ""} onClick={() => setChartType("pie")} title="Pasta">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>
                </button>
              </div>
            )}

            <button className="pbi-btn-export" onClick={handleExportExcel} disabled={exporting || !result || result.rows.length === 0}>
              {exporting ? <span className="pbi-spinner dark" /> : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              )}
              Excel
            </button>

            <button className="pbi-btn-secondary" onClick={() => setShowSaveTemplate(true)} disabled={!selectedDataSource}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Kaydet
            </button>

            <button className="pbi-btn-secondary" onClick={() => setShowTemplates(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Şablonlar <span className="pbi-template-count">{templates.length}</span>
            </button>
          </div>
        </div>

        {/* Canvas content */}
        <div className="pbi-canvas-body">
          {!result && !loading && (
            <div className="pbi-empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              <h2>Rapor Oluşturun</h2>
              <p>Sol panelden veri kaynağı ve kolonları seçin, ardından <strong>Çalıştır</strong>'a tıklayın.</p>
            </div>
          )}

          {loading && !result && <PageLoader message="Rapor çalıştırılıyor..." />}

          {result && (
            <>
              {/* KPI Cards */}
              {kpis.length > 0 && (
                <div className="pbi-kpi-row">
                  {kpis.map((kpi, i) => (
                    <div key={i} className="pbi-kpi-card" style={{ borderLeftColor: kpi.color }}>
                      <span className="pbi-kpi-value">{kpi.value}</span>
                      <span className="pbi-kpi-label">{kpi.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Charts */}
              {viewMode !== "table" && (() => {
                const numericCols = result.columns.filter((col) => isNumericColumn(col));
                const labelCol = result.columns.find((col) => isStringColumn(col));

                if (numericCols.length === 0) {
                  return (
                    <div className="pbi-tile">
                      <div className="pbi-tile-body pbi-chart-empty">
                        <p>Grafik gösterimi için sayısal veri bulunamadı</p>
                      </div>
                    </div>
                  );
                }

                const allNumericKeys = numericCols.map((col) => ({
                  key: getColumnDisplayName(col) || col,
                  field: col,
                }));

                // Auto-select first metric if none selected
                const activeMetrics = selectedChartMetrics.length > 0
                  ? selectedChartMetrics.filter((m) => allNumericKeys.some((k) => k.key === m))
                  : [allNumericKeys[0].key];

                const truncateLabel = (str: string, max: number = 18) =>
                  str.length > max ? str.slice(0, max) + "…" : str;

                // Aggregate rows by label to avoid duplicate X-axis entries
                let chartData: Record<string, any>[];
                if (labelCol) {
                  const grouped = new Map<string, Record<string, number>>();
                  result.rows.forEach((row) => {
                    const rawLabel = String(row[labelCol] ?? "-");
                    const label = truncateLabel(rawLabel);
                    if (!grouped.has(label)) {
                      const entry: Record<string, number> = {};
                      allNumericKeys.forEach(({ key }) => { entry[key] = 0; });
                      grouped.set(label, entry);
                    }
                    const entry = grouped.get(label)!;
                    allNumericKeys.forEach(({ key, field }) => {
                      const raw = row[field];
                      entry[key] += raw != null ? Number(raw) || 0 : 0;
                    });
                  });
                  // Sort by first active metric descending
                  const sortKey = activeMetrics[0] || allNumericKeys[0].key;
                  chartData = Array.from(grouped.entries())
                    .sort((a, b) => (b[1][sortKey] || 0) - (a[1][sortKey] || 0))
                    .map(([name, vals]) => ({ name, ...vals }));
                } else {
                  chartData = result.rows.map((row, rowIdx) => {
                    const item: Record<string, any> = { name: `#${rowIdx + 1}` };
                    allNumericKeys.forEach(({ key, field }) => {
                      const raw = row[field];
                      item[key] = raw != null ? Number(raw) : 0;
                    });
                    return item;
                  });
                }

                const customTooltipFormatter: any = (value: any) => formatNumber(Number(value));

                // For pie: group small slices as "Diğer"
                const buildPieData = (metricKey: string) => {
                  return [...chartData].sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0));
                };

                const toggleMetric = (key: string) => {
                  setSelectedChartMetrics((prev) => {
                    if (prev.includes(key)) {
                      const next = prev.filter((m) => m !== key);
                      return next.length === 0 ? [key] : next; // keep at least 1
                    }
                    return [...prev, key];
                  });
                };

                return (
                  <div className="pbi-tile">
                    <div className="pbi-tile-head">
                      <div>
                        <span className="pbi-tile-title">
                          {chartType === "bar" ? "Sütun Grafik" : chartType === "line" ? "Çizgi Grafik" : chartType === "area" ? "Alan Grafik" : "Pasta Grafik"}
                        </span>
                        <span className="pbi-tile-subtitle" style={{ marginLeft: 8 }}>
                          {chartData.length} grup / {result.rows.length} kayıt
                        </span>
                      </div>
                    </div>
                    {/* Metric selector chips */}
                    {allNumericKeys.length > 1 && (
                      <div className="pbi-metric-bar">
                        <span className="pbi-metric-bar-label">Metrik:</span>
                        {allNumericKeys.map(({ key }, idx) => (
                          <button
                            key={key}
                            className={`pbi-metric-chip ${activeMetrics.includes(key) ? "active" : ""}`}
                            style={activeMetrics.includes(key) ? { background: CHART_COLORS[idx % CHART_COLORS.length], borderColor: CHART_COLORS[idx % CHART_COLORS.length] } : {}}
                            onClick={() => toggleMetric(key)}
                          >
                            {key}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="pbi-tile-body">
                      {chartType === "pie" ? (
                        <div className="pbi-pie-grid" key="pie">
                          {activeMetrics.slice(0, 2).map((key) => {
                            const pieData = buildPieData(key);
                            return (
                              <div key={key} className="pbi-pie-item">
                                <h4>{key}</h4>
                                <ResponsiveContainer width="100%" height={300}>
                                  <PieChart>
                                    <Pie
                                      data={pieData}
                                      dataKey={key}
                                      nameKey="name"
                                      cx="50%"
                                      cy="50%"
                                      outerRadius={100}
                                      innerRadius={50}
                                      paddingAngle={2}
                                      label={({ name, percent }: any) => percent > 0.05 ? `${name}: %${((percent || 0) * 100).toFixed(0)}` : ""}
                                      labelLine={true}
                                    >
                                      {pieData.map((_, idx) => (
                                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={customTooltipFormatter} />
                                    <Legend verticalAlign="bottom" height={36} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <ResponsiveContainer key={`${chartType}-${activeMetrics.join(",")}`} width="100%" height={Math.max(360, chartData.length * 18)}>
                          {chartType === "bar" ? (
                            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 50 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#5f6368" }} angle={-45} textAnchor="end" height={90} interval={chartData.length > 30 ? Math.floor(chartData.length / 30) : 0} />
                              <YAxis tick={{ fontSize: 11, fill: "#5f6368" }} tickFormatter={(v) => formatNumber(v)} width={60} />
                              <Tooltip formatter={customTooltipFormatter} />
                              {activeMetrics.length > 1 && <Legend wrapperStyle={{ paddingTop: 8 }} />}
                              {activeMetrics.map((key) => {
                                const idx = allNumericKeys.findIndex((k) => k.key === key);
                                return <Bar key={key} dataKey={key} fill={CHART_COLORS[idx % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />;
                              })}
                            </BarChart>
                          ) : chartType === "line" ? (
                            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 50 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#5f6368" }} angle={-45} textAnchor="end" height={90} interval={chartData.length > 30 ? Math.floor(chartData.length / 30) : 0} />
                              <YAxis tick={{ fontSize: 11, fill: "#5f6368" }} tickFormatter={(v) => formatNumber(v)} width={60} />
                              <Tooltip formatter={customTooltipFormatter} />
                              {activeMetrics.length > 1 && <Legend wrapperStyle={{ paddingTop: 8 }} />}
                              {activeMetrics.map((key) => {
                                const idx = allNumericKeys.findIndex((k) => k.key === key);
                                return <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[idx % CHART_COLORS.length]} strokeWidth={2.5} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />;
                              })}
                            </LineChart>
                          ) : (
                            <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 50 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#5f6368" }} angle={-45} textAnchor="end" height={90} interval={chartData.length > 30 ? Math.floor(chartData.length / 30) : 0} />
                              <YAxis tick={{ fontSize: 11, fill: "#5f6368" }} tickFormatter={(v) => formatNumber(v)} width={60} />
                              <Tooltip formatter={customTooltipFormatter} />
                              {activeMetrics.length > 1 && <Legend wrapperStyle={{ paddingTop: 8 }} />}
                              {activeMetrics.map((key) => {
                                const idx = allNumericKeys.findIndex((k) => k.key === key);
                                return <Area key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[idx % CHART_COLORS.length]} fill={CHART_COLORS[idx % CHART_COLORS.length]} fillOpacity={0.15} strokeWidth={2} />;
                              })}
                            </AreaChart>
                          )}
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Table */}
              {viewMode !== "chart" && (
                <div className="pbi-tile">
                  <div className="pbi-tile-head">
                    <span className="pbi-tile-title">Veri Tablosu</span>
                    <span className="pbi-tile-subtitle">{result.totalCount} toplam kayıt</span>
                  </div>
                  <div className="pbi-tile-body pbi-tile-table-body">
                    {result.rows.length > 0 ? (
                      <>
                        <div className="pbi-table-wrapper">
                          <table className="pbi-table">
                            <thead>
                              <tr>
                                {result.columns.map((col) => (
                                  <th key={col}>{getColumnDisplayName(col) || col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {result.rows.map((row, rIdx) => (
                                <tr key={rIdx}>
                                  {result.columns.map((col) => (
                                    <td key={col}>
                                      {reportMode === "grouped" ? formatGroupedCell(row[col], col) : formatCell(row[col], col)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {result.totalPages > 1 && (
                          <div className="pbi-pagination">
                            <button disabled={!result.hasPreviousPage || loading} onClick={() => executeReport(pageNumber - 1)}>← Önceki</button>
                            <span>Sayfa {result.pageNumber} / {result.totalPages}</span>
                            <button disabled={!result.hasNextPage || loading} onClick={() => executeReport(pageNumber + 1)}>Sonraki →</button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="pbi-tile-empty"><p>Sonuç bulunamadı</p></div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ─── OVERLAYS (Templates, Save, Alert, Confirm) ─── */}
      {showTemplates && (
        <div className="pbi-overlay" onClick={() => setShowTemplates(false)}>
          <div className="pbi-panel" onClick={(e) => e.stopPropagation()}>
            <div className="pbi-panel-head">
              <h2>Kayıtlı Şablonlar</h2>
              <button className="pbi-btn-close" onClick={() => setShowTemplates(false)}>✕</button>
            </div>
            <div className="pbi-panel-body">
              {loadingTemplates ? (
                <p className="pbi-hint">Yükleniyor...</p>
              ) : templates.length === 0 ? (
                <div className="pbi-tile-empty"><p>Henüz kayıtlı şablon yok</p></div>
              ) : (
                <div className="pbi-template-list">
                  {templates.map((t) => (
                    <div key={t.reportTemplateId} className="pbi-template-card">
                      <div className="pbi-template-info">
                        <h4>{t.name}</h4>
                        {t.description && <p>{t.description}</p>}
                        <div className="pbi-template-meta">
                          <span className="pbi-tag ds">{t.dataSource}</span>
                          {t.isPublic && <span className="pbi-tag public">Herkese Açık</span>}
                          {t.createdByUserName && <span className="pbi-tag author">{t.createdByUserName}</span>}
                        </div>
                      </div>
                      <div className="pbi-template-actions">
                        <button className="pbi-btn-sm run" onClick={() => handleRunTemplate(t)} title="Çalıştır">▶</button>
                        <button className="pbi-btn-sm load" onClick={() => handleLoadTemplate(t)} title="Yükle">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/><path d="M4 19V5"/></svg>
                        </button>
                        <button className="pbi-btn-sm export" onClick={() => handleExportTemplate(t)} title="Excel">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        </button>
                        <button className="pbi-btn-sm delete" onClick={() => handleDeleteTemplate(t)} title="Sil">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showSaveTemplate && (
        <div className="pbi-overlay" onClick={() => setShowSaveTemplate(false)}>
          <div className="pbi-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Şablon Olarak Kaydet</h3>
            <div className="pbi-modal-form">
              <div className="pbi-form-group">
                <label>Şablon Adı <span className="required">*</span></label>
                <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Örn: Firma Bazlı Sipariş Özeti" />
              </div>
              <div className="pbi-form-group">
                <label>Açıklama</label>
                <textarea value={templateDesc} onChange={(e) => setTemplateDesc(e.target.value)} placeholder="Rapor açıklaması" rows={2} />
              </div>
              <div className="pbi-form-group">
                <label className="pbi-checkbox-label">
                  <input type="checkbox" checked={templatePublic} onChange={(e) => setTemplatePublic(e.target.checked)} />
                  Herkese açık şablon
                </label>
              </div>
            </div>
            <div className="pbi-modal-actions">
              <button className="pbi-btn-cancel" onClick={() => setShowSaveTemplate(false)}>İptal</button>
              <button className="pbi-btn-execute" onClick={handleSaveTemplate}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
      />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default DynamicReports;
